import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, AlertIcon, Box, Text } from '@chakra-ui/react';

import {
  clearMaintenance,
  selectMaintenanceActive,
  selectMaintenanceEndsAt,
  selectMaintenanceMessage,
  selectMaintenanceStartsAt
} from '../../redux/modules/maintenance';

import './styles.scss';

/**
 * formatWindowTime
 *
 * Returns a human-readable string for a maintenance window boundary in both
 * local timezone and UTC.
 *
 * @param {string|null} isoString - ISO-8601 UTC timestamp
 * @returns {string|null}
 */
function formatWindowTime(isoString) {
  if (!isoString) return null;

  const date = new Date(isoString);

  const localStr = date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  });

  const utcStr = date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short'
  });

  if (localStr === utcStr) return localStr;
  return `${localStr} (${utcStr})`;
}

/**
 * getCountdownLabel
 *
 * Returns a human-friendly countdown string to a future date, or null if the
 * date is in the past or not provided.
 *
 * @param {string|null} isoString
 * @returns {string|null}
 */
function getCountdownLabel(isoString) {
  if (!isoString) return null;

  const diffMs = new Date(isoString) - new Date();

  if (diffMs <= 0) return null;

  const totalMinutes = Math.ceil(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m remaining`;
  if (hours > 0) return `${hours}h remaining`;
  return `${minutes}m remaining`;
}

/**
 * MaintenanceBanner
 *
 * A persistent, non-dismissable warning banner that renders at the top of
 * every authenticated page while `state.maintenance.active === true`.
 *
 * Auto-clears from the store when the local timer detects that ends_at has
 * passed.  Re-renders once per minute to keep the countdown fresh.
 */
const MaintenanceBanner = () => {
  const dispatch = useDispatch();
  const active = useSelector(selectMaintenanceActive);
  const message = useSelector(selectMaintenanceMessage);
  const startsAt = useSelector(selectMaintenanceStartsAt);
  const endsAt = useSelector(selectMaintenanceEndsAt);

  const [countdown, setCountdown] = useState(() => getCountdownLabel(endsAt));

  useEffect(() => {
    if (!active || !endsAt) return undefined;

    const tick = () => {
      const remaining = new Date(endsAt) - new Date();

      if (remaining <= 0) {
        dispatch(clearMaintenance());
        return;
      }

      setCountdown(getCountdownLabel(endsAt));
    };

    // Update immediately then every 60 seconds
    tick();
    const intervalId = setInterval(tick, 60000);

    return () => clearInterval(intervalId);
  }, [active, endsAt, dispatch]);

  if (!active) return null;

  const startLabel = formatWindowTime(startsAt);
  const endLabel = formatWindowTime(endsAt);

  return (
    <div className="MaintenanceBanner">
      <Alert
        aria-live="polite"
        role="status"
        status="warning"
      >
        <AlertIcon />
        <Box>
          <Text fontWeight="semibold">
            {message || 'PINS is currently undergoing scheduled maintenance.'}
          </Text>
          {(startLabel || endLabel) && (
            <Text fontSize="sm">
              {startLabel && endLabel && `Scheduled window: ${startLabel} – ${endLabel}`}
              {startLabel && !endLabel && `Started: ${startLabel}`}
              {!startLabel && endLabel && `Expected completion: ${endLabel}`}
              {countdown && ` · ${countdown}`}
            </Text>
          )}
        </Box>
      </Alert>
    </div>
  );
};

export default MaintenanceBanner;
